import React, { FunctionComponent, useState } from "react";
import { Box, DialogContent, Divider, Skeleton, Toolbar } from "@mui/material";
import styles from "./styles.module.scss";
import { LazyImageAspectFit } from "../LazyImageAspectFit";
import { strings } from "../../localization/LocalizedStrings";
import { saveAs } from "file-saver";
import { ToolbarBox } from "../toolbar/ToolbarBox";
import { ToolbarRawButton } from "../toolbar/ToolbarRawButton";
import { Buffer } from "buffer";
import { useGetImageSignedUrl } from "../../hooks/images/useGetImageSignedUrl";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { PiperDialog } from "../PiperDialog";
import { IconType } from "../../icons";

export const ImageDetailDialog: FunctionComponent<IImageDetailDialog> = ({
  isPublic,
  uuid,
  title,
  message,
  isOpen,
  setIsOpen,
  image,
  filename,
  onStyleButtonClicked
}) => {
  const [styleToggle, setStyleToggle] = useState(true);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const { getImageSignedUrl } = useGetImageSignedUrl();
  const downloadImage = async () => {
    if (!image) return;
    if (isPublic) {
      const buffer = Buffer.from(image, "base64");
      const blob = new Blob([new Uint8Array(buffer)], { type: "image/png" });
      saveAs(blob, filename);
    } else {
      await startDownload();
    }
  };
  const startDownload = async () => {
    setIsLoadingDownload(true);
    const { status, body } = await getImageSignedUrl(uuid);
    if (StatusCodesHelper.isSuccessful(status)) {
      saveAs(body, filename);
    }
    setIsLoadingDownload(false);
  };
  return (
    <PiperDialog
      fullWidth
      maxWidth={"sm"}
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      <Toolbar>
        <h3>{title}</h3>
        <Box flexGrow={1} />
        <ToolbarBox className={styles.toolbarBox}>
          {onStyleButtonClicked && (
            <ToolbarRawButton
              tooltip={strings.toggle_style}
              iconType={IconType.Palette}
              filled={styleToggle}
              onClick={() => {
                setStyleToggle(state => !state);
                onStyleButtonClicked();
              }}
            />
          )}
          {onStyleButtonClicked && <Divider orientation="vertical" flexItem />}
          <ToolbarRawButton
            onClick={() => downloadImage()}
            loading={isLoadingDownload}
            tooltip={strings.download}
            iconType={IconType.Download}
          />
          <Divider orientation="vertical" flexItem />
          <ToolbarRawButton
            onClick={() => setIsOpen(false)}
            tooltip={strings.close}
            loading={!image}
            iconType={IconType.Close}
          />
        </ToolbarBox>
      </Toolbar>
      <DialogContent className={styles.content}>
        {message && <p className={styles.message}>{message}</p>}
        {image ? (
          <LazyImageAspectFit alt={"image"} image={image} defaultImage={image} canMagnify />
        ) : (
          <Skeleton variant="rectangular" sx={{ aspectRatio: "1", height: "auto" }} />
        )}
      </DialogContent>
    </PiperDialog>
  );
};

export interface IImageDetailDialog {
  isPublic: boolean;
  uuid: string;
  title: string;
  message?: string;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  image?: string;
  filename: string;
  onStyleButtonClicked?: () => void;
}
